// #4504: put a time limit on every wait that happens INSIDE one CouchDB request.
//
// The failure this exists for: `AuthService.getFetchFunction()` wraps every request
// PouchDB makes to CouchDB, and advertises one ceiling on a hung connection —
// `FETCH_TIMEOUT_MS`, 90 seconds. That ceiling is armed late. The AbortController and its
// timer are created just before the `PouchDB.fetch` call at the bottom of each retry
// iteration, but earlier in the SAME iteration the code waits on the shared couch-login
// promise, and on a session refresh, with no bound at all. So the 90 seconds described
// only the last leg of the work, not the request. A login that never came back left every
// request behind it waiting for ever.
//
// The login could really stay unsettled: `attemptCouchLoginAsync`'s try/finally means a
// THROW can never strand it, but the inner call could take unbounded TIME. Its probe, its
// Firebase wait, its cloud-function call and its retry sleeps were all bounded already.
// What was not: every call to CouchDB's `/_session` endpoint, and every native cookie-jar
// clear. Those are what this module closes.
//
// What this does NOT do, and cannot: cancel work it has stopped waiting for. Racing a
// timer abandons the wait; the login itself carries on in the background. That is fine
// here — every caller has somewhere safe to go, namely straight on to the fetch, which
// then either succeeds on the cookie it already had or fails and hands PouchDB a real
// response to retry. Same reasoning, and the same latch-and-clear shape, as
// `local-store-timeout.ts`.
//
// Keep this module free of Angular and browser-only imports: the unit test loads it under
// plain node and injects its own timers and fetch. Same rule as `local-store-timeout.ts`
// and the other `couch-login-*.ts` modules beside it.

import { createTimeoutSignal } from './timeout-signal';
import { isTransientFetchError } from './error-patterns';
import { limitFiredBecauseOfFreeze, startRunningClockTicks } from './running-clock';

/**
 * How long a CouchDB request waits for an already-running couch login before giving up on
 * it and going to the network anyway.
 *
 * This is a BACKSTOP, not a working budget. Every await inside the login is separately
 * bounded (see `COUCH_LOGIN_CONSTRUCTION_CEILING_MS`), so a login that is merely slow
 * finishes well inside this. The bound exists for the day somebody adds a new, unbounded
 * await to the login path — without it, that change would silently reopen the hole this
 * module was written to close, and nothing would report it.
 *
 * 150 seconds, so that it clears `COUCH_LOGIN_CONSTRUCTION_CEILING_MS` with room to spare.
 * That is the whole basis for the number: a login on a genuinely terrible network can take
 * about 120 s of legitimate, bounded work, and a backstop below that would report an
 * ordinary slow login as if something impossible had happened — which is how a canary turns
 * into noise nobody reads.
 *
 * It is deliberately NOT a shared budget with `FETCH_TIMEOUT_MS` in `auth.service.ts`, and
 * one call to the fetch wrapper can spend this bound THREE times — at the top of each of
 * the two retry iterations, and once more in the relogin branch, all waiting on the same
 * shared login promise — plus both 90 s fetches. So a single stuck request can sit for
 * something over ten minutes before anything gives up on it. That is accepted, and it is
 * the cost of the alternative being rejected: `FETCH_TIMEOUT_MS` was chosen to sit above
 * CouchDB's ~60 s longpoll cycle, so splitting one budget across the wait and the fetch
 * would hand a longpoll whatever was left over, abort healthy idle connections, and push
 * PouchDB's continuous replication into backoff. Finite and slow beats infinite, and
 * PouchDB owns the retry either way.
 *
 * This and the two bounds below stay on wall time. They guard network and native calls, where
 * giving up on a call that was open across a freeze and trying again is the right recovery.
 * #6096 (2026-10-01) found no report of this one firing after a freeze, but the first such
 * reports arrived that same day (Sentry CYBERNETIC-FITNESS-APP-4HB, df-f59581ef5a04): the phone
 * froze the app while a login ran, and the timer fired on waking. So this wait still gives up
 * on wall time, and a running-time clock ticks beside it purely to say whether a freeze caused
 * the give-up (`BoundedWaitResult.freezeCaused`), so the report can tell a freeze from a hang.
 * design-docs/boot-sequence.md, "#6096".
 */
export const COUCH_LOGIN_WAIT_TIMEOUT_MS = 150_000;

/**
 * How long one call to CouchDB's `/_session` endpoint may take.
 *
 * These calls are small and best-effort — the login has already succeeded by the time the
 * warmup runs, and the session cookie self-heals on the next request's 401 retry. So this
 * is not a performance budget; its only job is to tell "working" apart from "never coming
 * back".
 */
export const SESSION_ENDPOINT_TIMEOUT_MS = 10_000;

/**
 * How long the native cookie-jar clear may take before the login stops waiting for it.
 *
 * Shorter than the `/_session` bound because it is a local bridge call with no network in
 * it — anything slower than a couple of seconds is the bridge not answering, not work in
 * progress. Skipping the clear degrades into a stale cookie, which the 401 retry path
 * already handles.
 */
export const COOKIE_CLEAR_TIMEOUT_MS = 3_000;

/**
 * The longest a couch login can take when every one of its own waits runs to its limit.
 *
 * MIRRORED NUMBERS — each part below is a copy of a limit that lives somewhere else, named
 * in its own comment. When one of those moves, change it here too, or the relationship this
 * module pins stops describing the real code. `couch-login-wait-bound.test.ts` pins the
 * relationship, not the individual numbers, so a drift shows up as the backstop no longer
 * clearing the ceiling.
 */
export const COUCH_LOGIN_CONSTRUCTION_CEILING_MS =
  // the offline re-probe — REACHABILITY_PROBE_TIMEOUT_MS in couch-login-online-gate.ts
  10_000
  // waiting for Firebase to restore the session — the 5000 passed to
  // awaitFirebaseUserPresentAsync in auth.service.ts's attemptCouchLogin_innerAsync
  + 5_000
  // the retry loop. retryDelays = [2000, 5000, 10000] in attemptCouchLogin_innerAsync gives
  // both the number of passes and the sleep after each failed one. Every pass calls the
  // couchLogin cloud function and can clear the native cookie jar before failing, so both
  // are charged to every pass; the one /_session call happens only on the pass that
  // succeeds, so it is charged once.
  //
  // ONE `couchLoginAsync()` COSTS FAR MORE THAN ITS `timeoutMs`. That 5_000
  // (cloud-functions.service.ts) bounds the fetch alone. Around it, `callApiAsync` first
  // races Firebase's `authStateReady()` against its own 5 s timer on every authenticated
  // call, and the non-resilient `callable` wrapper retries ONCE on a retriable non-timeout
  // error after a 3 s sleep — a second attempt that pays both again. So the worst is
  // (5 + 5) + 3 + (5 + 5) = 23 s per pass, not 5. Getting this wrong is what made an
  // earlier version of this constant read 66 s when the real figure is 120 s, which nearly
  // shipped a backstop BELOW the ceiling it is supposed to clear.
  //
  // TWO THINGS ARE NOT MODELLED, so read this as the steady-state worst case rather than a
  // proof about every possible boot:
  //
  //  - `user.getIdToken()` on the same path carries no app-side bound at all (the Firebase
  //    SDK bounds it in practice). Nothing here can charge a limit that does not exist.
  //  - `getApiOriginAsync()` waits up to `BACKEND_ROUTE_WAIT_MS` (15 s,
  //    util/backend-origin.ts) for the backend route. Excluded because it is paid at most
  //    while that route is still unresolved: the gate returns instantly once it has an
  //    answer, and it only exists on the native prod bundle. Charging it to every attempt
  //    would put the modelled figure above the backstop — but doing so would describe a
  //    first-ever native boot whose route probe never answers, not the path a login
  //    normally takes. If the gate ever stops resolving once per boot, that reasoning
  //    fails and this constant, and the backstop above it, both need revisiting.
  //
  // So: a number to size the backstop against and to notice drift in, not a guarantee.
  + 3 * ((5_000 + 5_000) + 3_000 + (5_000 + 5_000) + COOKIE_CLEAR_TIMEOUT_MS)
  + (2_000 + 5_000 + 10_000)
  + SESSION_ENDPOINT_TIMEOUT_MS;

/** What a bounded wait ended up doing. `timedOut` is the only thing a caller must branch on. */
export interface BoundedWaitResult<T> {
  /** true when the time limit won and the caller stopped waiting; the work itself carries on. */
  timedOut: boolean;
  /** the awaited value — only meaningful when `timedOut` is false. */
  value?: T;
  /** how long the caller actually waited, for the report it files on a timeout. */
  waitedMs: number;
  /**
   * true when the time limit fired only because the phone froze the app during the wait: the
   * page had run for more than one tick less than the limit (`limitFiredBecauseOfFreeze`).
   * Always false when the work settled first. A caller reports a freeze-caused give-up as
   * the freeze it was, not as a hang.
   */
  freezeCaused: boolean;
  /** time the page was not running while the caller waited, as far as the running clock saw. */
  notRunningMs: number;
}

export type ScheduleTimeout = (cb: () => void, ms: number) => unknown;
export type ClearTimeoutFn = (handle: unknown) => void;
export type NowMs = () => number;

/**
 * Wait for `work`, but no longer than `timeoutMs`.
 *
 * Never rejects because of the time limit — it RESOLVES with `timedOut: true`, because
 * every caller here wants to carry on rather than fail. A rejection from `work` itself
 * still propagates, so a genuine error is not swallowed.
 *
 * The result is latched, so a value arriving after the limit is ignored rather than
 * resolving a promise the caller has already given up on, and both timers are always
 * cleared so they cannot leak.
 *
 * The limit is on wall time. Beside it a running clock ticks (`startRunningClockTicks`),
 * only to say afterwards whether a freeze caused the timeout (df-f59581ef5a04, the same
 * shape as `timeoutNotingFreezeAsync`). `runningNowMs` is that clock's time source: pass a
 * monotonic clock (`performance.now`), because a stepped wall clock could invent or hide a
 * gap. It defaults to `nowMs` for tests that drive one clock by hand.
 */
export function awaitBounded<T>(
  work: Promise<T>,
  timeoutMs: number,
  scheduleTimeout: ScheduleTimeout,
  clearTimeoutFn: ClearTimeoutFn,
  nowMs: NowMs,
  runningNowMs: NowMs = nowMs,
): Promise<BoundedWaitResult<T>> {
  const startedAt = nowMs();
  // wrap each function, never pass it as is: the ticks call them as methods of this object,
  // and the browser's own setTimeout/clearTimeout throw "Illegal invocation" when called that
  // way (awaitBoundedLive passes exactly those). Node's do not, so only a browser shows it.
  const ticks = startRunningClockTicks({
    nowMs: () => runningNowMs(),
    setTimeout: (cb, ms) => scheduleTimeout(cb, ms),
    clearTimeout: handle => clearTimeoutFn(handle),
  });
  return new Promise<BoundedWaitResult<T>>((resolve, reject) => {
    let settled = false;

    const handle = scheduleTimeout(() => {
      if (settled) return;
      settled = true;
      // stop() takes the last reading, at the moment the limit fired
      const runningMs = ticks.stop();
      resolve({
        timedOut: true,
        waitedMs: nowMs() - startedAt,
        freezeCaused: limitFiredBecauseOfFreeze(timeoutMs, runningMs),
        notRunningMs: ticks.clock.notRunningMs(),
      });
    }, timeoutMs);
    work.then(
      value => {
        if (settled) return;
        settled = true;
        clearTimeoutFn(handle);
        ticks.stop();
        resolve({
          timedOut: false, value, waitedMs: nowMs() - startedAt,
          freezeCaused: false, notRunningMs: ticks.clock.notRunningMs(),
        });
      },
      err => {
        if (settled) return;
        settled = true;
        clearTimeoutFn(handle);
        ticks.stop();
        reject(err);
      },
    );
  });
}

/**
 * The bound every caller in the running app uses: real timers, real clock.
 *
 * It exists because the timer and clock arguments were otherwise written out at each call
 * site, and the one thing a reader must be able to check at a glance is that the call sites
 * all wait the same way. The full form stays exported for the tests, which inject their own
 * timers. Same split, for the same reason, as `withBootLocalStoreTimeout` in
 * `local-store-timeout.ts`.
 */
export function awaitBoundedLive<T>(work: Promise<T>, timeoutMs: number): Promise<BoundedWaitResult<T>> {
  return awaitBounded(work, timeoutMs, setTimeout, clearTimeout, Date.now, () => performance.now());
}

/**
 * Call CouchDB's `/_session` endpoint with a time limit.
 *
 * Every `/_session` call in the app goes through here. That is the point of the helper:
 * each call site used to write its own bare fetch, and one of them — the session refresh
 * inside `getFetchFunction` itself — was missed when the others were first looked at.
 * Routing them all through one function means a new call site is bounded because of how it
 * is written, not because somebody remembered.
 *
 * On the time limit the fetch rejects with a `TimeoutError`. That is deliberate and is what
 * each caller's existing error handling then reports: `isTransientFetchError` in
 * `error-patterns.ts` does NOT count a TimeoutError as an expected network blip (its test
 * pins that on purpose), so a `/_session` call that HANGS surfaces, while one that fails
 * fast on a flaky network stays quiet as before.
 *
 * The signal alone is NOT enough on the native apps (df-843aaf530c64). With CapacitorHttp
 * enabled, the bridge's patched fetch passes the signal on only for GET, HEAD, OPTIONS and
 * TRACE; a POST or DELETE goes to the native request without it, so the warmup, the refresh
 * and the sign-out had no limit there at all. So the wait is also raced against that same
 * signal: when it fires, this rejects with its reason whether or not the fetch listened. The
 * signal is still passed, so the web build aborts the real request. On native the abandoned
 * request carries on in the background, and a late answer only sets a cookie. Do not switch
 * to the browser's own fetch to get a working signal (#4038 did that elsewhere): on native
 * the patched fetch is what puts CouchDB's cookie into the jar PouchDB's requests use.
 *
 * A TimeoutError from here does not always mean a hang (df-7d52c9268cbf). The request goes
 * through the app-wide fetch wrapper, which holds a fast network failure while it checks
 * whether the device is online, so the limit can win against a request that had already
 * failed. When the limit fires, the abandoned request's real outcome is kept with the error;
 * the reports read it with `sessionRequestOutcomeOf`. The caller still gets the TimeoutError
 * at the limit, so nothing about when the login moves on changes.
 */
export function fetchSessionEndpointAsync(
  fetchFn: typeof fetch,
  url: string,
  init: RequestInit,
  timeoutMs: number = SESSION_ENDPOINT_TIMEOUT_MS,
  outcomeWaitMs: number = SESSION_REQUEST_OUTCOME_WAIT_MS,
): Promise<Response> {
  const signal = createTimeoutSignal(timeoutMs);
  const sentAtMs = Date.now();
  const request = fetchFn(url, { ...init, signal });
  // what the request itself ended up doing, whenever it does; never rejects
  const settled: Promise<SessionRequestOutcome> = request.then(
    response => ({ kind: SessionRequestOutcomeKind.Resolved, status: response.status, settledMs: Date.now() - sentAtMs }),
    error => ({ kind: SessionRequestOutcomeKind.Rejected, error, settledMs: Date.now() - sentAtMs }),
  );
  let onAbort: (() => void) | undefined;
  const timedOut = new Promise<never>((_resolve, reject) => {
    onAbort = () => {
      const reason = signal.reason ?? new DOMException('Signal timed out', 'TimeoutError');
      // df-7d52c9268cbf: keep the abandoned request's real outcome with the error, so the
      // report can tell a hang from a fast failure that the fetch wrapper was still holding
      if (typeof reason === 'object' && reason !== null) {
        sessionRequestOutcomes.set(reason, awaitSessionRequestOutcomeAsync(settled, sentAtMs, outcomeWaitMs));
      }
      reject(reason);
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
  // if the fetch settles first, stop listening so the timeout cannot reject an abandoned promise
  void settled.then(() => signal.removeEventListener('abort', onAbort!));
  return Promise.race([request, timedOut]);
}

/**
 * How long, after a `/_session` call gave up at its own limit, the report waits for the
 * abandoned request to settle before calling it still pending (df-7d52c9268cbf).
 *
 * The request goes through the app-wide fetch wrapper, which holds a failure while it checks
 * whether the device is online (up to that check's own 10 s probe limit) and only then hands
 * it back. On the web the request has also been aborted by the same 10 s signal by then. So a
 * request that is not settled 25 s after the limit is one the signal could not stop: the
 * native POST or DELETE, which ignores it (df-843aaf530c64), on a real hang.
 */
export const SESSION_REQUEST_OUTCOME_WAIT_MS = 25_000;

/** What an abandoned `/_session` request ended up doing. */
export enum SessionRequestOutcomeKind {
  Rejected = 'rejected',
  Resolved = 'resolved',
  Pending = 'pending',
}

export interface SessionRequestOutcome {
  kind: SessionRequestOutcomeKind;
  /** the request's own error, when it rejected */
  error?: unknown;
  /** the HTTP status, when it answered */
  status?: number;
  /** wall time from sending the request until it settled, or until the wait gave up */
  settledMs: number;
}

// the outcome of each request that gave up at its limit, keyed by the error the caller got
const sessionRequestOutcomes = new WeakMap<object, Promise<SessionRequestOutcome>>();

/**
 * When `error` came from `fetchSessionEndpointAsync` giving up at its time limit, what the
 * abandoned request itself ended up doing. Settles within `SESSION_REQUEST_OUTCOME_WAIT_MS`
 * of the limit and never rejects. `undefined` for every other error, which is the request's
 * own and needs no waiting.
 */
export function sessionRequestOutcomeOf(error: unknown): Promise<SessionRequestOutcome> | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  return sessionRequestOutcomes.get(error);
}

/**
 * df-7d52c9268cbf: whether a `/_session` call that gave up at its limit was really a fast
 * network failure. The request had rejected with an error `isTransientFetchError` treats as
 * an ordinary blip, but the fetch wrapper was still holding it when the limit fired.
 */
export function wasHeldNetworkFailure(outcome: SessionRequestOutcome): boolean {
  return outcome.kind === SessionRequestOutcomeKind.Rejected && isTransientFetchError(outcome.error);
}

/** The fields a `/_session` timeout report adds, so a reader can tell a hang from a late failure. */
export function sessionRequestOutcomeFields(outcome: SessionRequestOutcome): {
  request_outcome: SessionRequestOutcomeKind;
  request_error_name?: string;
  request_status?: number;
  request_settled_ms: number;
} {
  return {
    request_outcome: outcome.kind,
    ...(outcome.kind === SessionRequestOutcomeKind.Rejected ? { request_error_name: (outcome.error as any)?.name } : {}),
    ...(outcome.kind === SessionRequestOutcomeKind.Resolved ? { request_status: outcome.status } : {}),
    request_settled_ms: Math.round(outcome.settledMs),
  };
}

// wait for the abandoned request, but no longer than `outcomeWaitMs` after the limit fired
function awaitSessionRequestOutcomeAsync(
  settled: Promise<SessionRequestOutcome>, sentAtMs: number, outcomeWaitMs: number,
): Promise<SessionRequestOutcome> {
  let handle: ReturnType<typeof setTimeout> | undefined;
  const pending = new Promise<SessionRequestOutcome>(resolve => {
    handle = setTimeout(
      () => resolve({ kind: SessionRequestOutcomeKind.Pending, settledMs: Date.now() - sentAtMs }),
      outcomeWaitMs,
    );
  });
  void settled.then(() => clearTimeout(handle));
  return Promise.race([settled, pending]);
}
